import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Table, TableBody, TableHead, Toolbar, Typography, CircularProgress,
    TablePagination, Chip, Box, FormControl, InputLabel, Select, MenuItem, Button,
    Snackbar, Alert, Tabs, Tab, TextField, InputAdornment, IconButton
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { listSupportTickets, searchSupportTickets, getSupportTaxonomy, SupportCategory } from "../services/supportService";
import { socketService } from "../services/socketService";
import { getAdminUidFromToken } from "../utils/auth";
import { SupportTicket } from "../types/Support";
import {
    SUPPORT_ISSUE_TYPES, issueTypeLabel, TICKET_STATUSES, STATUS_LABELS,
    TICKET_PRIORITIES, PRIORITY_LABELS, PRIORITY_COLOR, isOpenStatus, QUEUE_LABELS,
} from "../constants/support";
import PageHelp from "../components/common/PageHelp";
import {
    StyledTableContainer, StyledTableCell, StyledTableRow, StyledPaper,
} from "../assets/styles/tableStyle"; // Assuming you reuse your styles

const PAGE_SIZE = 20;

// Map a date preset to ISO {from,to} bounds. Day-boundaries for today/yesterday; rolling N-day
// windows for last3/last7. Called at fetch time (uses the current clock), never memoized.
const dateRangeBounds = (preset: string): { from?: string; to?: string } => {
    if (!preset) return {};
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (preset) {
        case 'last1h':
            return { from: new Date(now.getTime() - 3600000).toISOString(), to: now.toISOString() };
        case 'last6h':
            return { from: new Date(now.getTime() - 6 * 3600000).toISOString(), to: now.toISOString() };
        case 'today':
            return { from: startOfToday.toISOString(), to: now.toISOString() };
        case 'yesterday': {
            const startOfYesterday = new Date(startOfToday.getTime() - 86400000);
            return { from: startOfYesterday.toISOString(), to: startOfToday.toISOString() };
        }
        case 'last3':
            return { from: new Date(now.getTime() - 3 * 86400000).toISOString(), to: now.toISOString() };
        case 'last7':
            return { from: new Date(now.getTime() - 7 * 86400000).toISOString(), to: now.toISOString() };
        default:
            return {};
    }
};

const statusColor = (status: string) => {
    switch (status) {
        case 'open': return 'error';
        case 'in_progress': return 'warning';
        case 'resolved': return 'success';
        case 'closed': return 'default';
        default: return 'default';
    }
};

const SupportTicketList: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Initial filters from URL (e.g., coming from Right-Click on Ride)
    const initialRideId = searchParams.get('rideId') ? Number(searchParams.get('rideId')) : undefined;

    const [items, setItems] = useState<SupportTicket[]>([]);
    const [page, setPage] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    // Filters
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [issueFilter, setIssueFilter] = useState<string>("");
    const [priorityFilter, setPriorityFilter] = useState<string>("");
    // Taxonomy v2 category filter (Safety, Trip, Fare + payment, Payouts, …).
    const [categoryFilter, setCategoryFilter] = useState<string>("");
    const [queueFilter, setQueueFilter] = useState<string>("");
    const [categories, setCategories] = useState<SupportCategory[]>([]);
    useEffect(() => { getSupportTaxonomy().then((r) => setCategories(r.data.categories || [])).catch(() => undefined); }, []);
    // Date preset: '' (any) | today | yesterday | last3 | last7. Day-boundaries for today/yesterday,
    // rolling windows for last 3/7 — computed browser-side (ops run in IST) into ISO bounds the API filters on.
    const [dateFilter, setDateFilter] = useState<string>("");

    // Queue tabs — All | My tickets | Unassigned. These COMPOSE with the dropdown filters above.
    const [queueTab, setQueueTab] = useState<'all' | 'mine' | 'unassigned'>('all');
    const handleQueueTab = (_: React.SyntheticEvent, v: string) => {
        setQueueTab(v as 'all' | 'mine' | 'unassigned');
        setPage(0);
    };

    // Search — debounced 400ms. While a query is active the table shows search results
    // (no pagination); ✕ clears back to the normal paginated queue.
    // F10: the user page links here with ?userId=<firebaseUid> — start with that person's tickets.
    const initialQuery = (searchParams.get('userId') || searchParams.get('q') || '').trim();
    const [searchInput, setSearchInput] = useState(initialQuery);
    const [activeQuery, setActiveQuery] = useState(initialQuery);
    const [searchRows, setSearchRows] = useState<SupportTicket[]>([]);
    const [searching, setSearching] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setActiveQuery(searchInput.trim()), 400);
        return () => clearTimeout(t);
    }, [searchInput]);
    useEffect(() => {
        if (!activeQuery) { setSearchRows([]); return; }
        let cancelled = false;
        setSearching(true);
        // Search composes with the date preset too, so "phone X in the last 7 days" narrows correctly.
        searchSupportTickets(activeQuery, dateRangeBounds(dateFilter))
            .then((res) => { if (!cancelled) setSearchRows(res.data.tickets || []); })
            .catch((err) => { console.error("Ticket search failed", err); if (!cancelled) setSearchRows([]); })
            .finally(() => { if (!cancelled) setSearching(false); });
        return () => { cancelled = true; };
    }, [activeQuery, dateFilter]);
    const searchActive = activeQuery.length > 0;

    // 30s heartbeat so the live SLA countdown chips re-render without any data refetch.
    const [, setSlaTick] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setSlaTick((x) => x + 1), 30000);
        return () => clearInterval(t);
    }, []);

    // Live escalation banner (SLA breach / safety) — the queue is where agents live, so surface
    // server-pushed escalations here.
    const [alertMsg, setAlertMsg] = useState<{ text: string; severity: 'warning' | 'error' } | null>(null);
    // Escalations stay 60s and CANNOT be dismissed for the first 10s (a 10s auto-hide was being
    // missed entirely when the agent was on another tab/page).
    const [alertLocked, setAlertLocked] = useState(false);
    useEffect(() => {
        if (!alertMsg) return;
        setAlertLocked(true);
        const t = setTimeout(() => setAlertLocked(false), 10000);
        return () => clearTimeout(t);
    }, [alertMsg]);

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listSupportTickets(page + 1, PAGE_SIZE, {
                status: statusFilter || undefined,
                issueType: issueFilter || undefined,
                priority: priorityFilter || undefined,
                category: categoryFilter || undefined,
                queue: queueFilter || undefined,
                rideId: initialRideId,
                // Tab scoping: My = my numeric staff id; Unassigned = the unclaimed pool.
                assigneeId: queueTab === 'mine' ? Number(getAdminUidFromToken()) : undefined,
                unassigned: queueTab === 'unassigned' ? true : undefined,
                ...dateRangeBounds(dateFilter),
            });
            setItems(res.data.data);
            setTotal(res.data.total);
        } catch (error) {
            console.error("Failed to fetch tickets", error);
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter, issueFilter, priorityFilter, categoryFilter, queueFilter, initialRideId, queueTab, dateFilter]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    // Keep a stable ref to the latest fetcher so the socket effect can refresh without
    // re-subscribing every time a filter changes.
    const fetchRef = useRef(fetchTickets);
    useEffect(() => { fetchRef.current = fetchTickets; }, [fetchTickets]);

    // Live escalations. Admin sockets auto-join the 'admins' room, where the SLA worker pages
    // 'support:slaBreach' and the safety pre-gate pages 'support:safetyEscalation'. Show a banner
    // and refresh the queue so a newly-escalated ticket surfaces without a manual reload.
    useEffect(() => {
        socketService.connect();
        const onSla = (p: any) => {
            const who = p?.publicNumber || (p?.ticketId ? `#${p.ticketId}` : 'a ticket');
            setAlertMsg({
                text: `SLA breach — ${who} (${p?.level === 'first_response' ? 'first response' : 'resolution'}) escalated to ${PRIORITY_LABELS[p?.priority] ?? p?.priority ?? 'higher'}`,
                severity: p?.isSafety ? 'error' : 'warning',
            });
            fetchRef.current();
        };
        const onSafety = (p: any) => {
            const where = p?.publicNumber ? ` on ${p.publicNumber}` : p?.chatRoomId ? ` in room ${p.chatRoomId}` : '';
            setAlertMsg({
                text: `SAFETY escalation${where} — a rider/driver flagged an urgent safety issue (now P0)`,
                severity: 'error',
            });
            fetchRef.current();
        };
        // Customer reopened a resolved ticket — same page-the-queue pattern as an SLA breach.
        const onReopened = (p: any) => {
            const who = p?.publicNumber || (p?.ticketId ? `#${p.ticketId}` : 'A ticket');
            setAlertMsg({
                text: `Ticket ${who} was REOPENED by the customer${p?.issueType ? ` (${issueTypeLabel(p.issueType)})` : ''}${p?.reopenCount ? ` — reopen #${p.reopenCount}` : ''}`,
                severity: 'warning',
            });
            fetchRef.current();
        };
        socketService.on('support:slaBreach', onSla);
        socketService.on('support:safetyEscalation', onSafety);
        socketService.on('support:ticketReopened', onReopened);
        return () => {
            socketService.off('support:slaBreach', onSla);
            socketService.off('support:safetyEscalation', onSafety);
            socketService.off('support:ticketReopened', onReopened);
        };
    }, []);

    // Live first-response SLA cell. Terminal tickets show '—'; responded ones a green check;
    // the rest a countdown chip that flips warning <15m and error once overdue. The 30s
    // heartbeat above keeps these fresh without refetching.
    const renderSla = (t: SupportTicket): React.ReactNode => {
        if (!isOpenStatus(t.status)) return '—';
        if (t.firstResponseAt) {
            return (
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    ✓ responded
                </Typography>
            );
        }
        if (!t.slaFirstDueAt) return '—';
        const msLeft = new Date(t.slaFirstDueAt).getTime() - Date.now();
        if (msLeft <= 0) {
            const overdueMin = Math.max(1, Math.floor(-msLeft / 60000));
            return <Chip size="small" color="error" label={`OVERDUE ${overdueMin}m`} />;
        }
        const minLeft = Math.ceil(msLeft / 60000);
        return (
            <Chip
                size="small"
                variant="outlined"
                color={msLeft < 15 * 60000 ? 'warning' : 'default'}
                label={`⏱ ${minLeft}m`}
            />
        );
    };

    const rows = searchActive ? searchRows : items;
    const busy = searchActive ? searching : loading;

    return (
        <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2 }}>
            <Toolbar sx={{ justifyContent: 'space-between' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="h6">
                            Support Tickets {initialRideId && `(Filtered for Ride #${initialRideId})`}
                        </Typography>
                        <PageHelp k="support" />
                    </Box>
                    {initialRideId && (
                        <Button size="small" onClick={() => navigate('/support-tickets')}>
                            Clear Ride Filter
                        </Button>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            {TICKET_STATUSES.map((s) => (
                                <MenuItem key={s} value={s}>{STATUS_LABELS[s] ?? s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Issue Type</InputLabel>
                        {/* Canonical codes now — the old payment_issue/ride_issue/… matched ZERO tickets. */}
                        <Select value={issueFilter} label="Issue Type" onChange={(e) => setIssueFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            {SUPPORT_ISSUE_TYPES.map((code) => (
                                <MenuItem key={code} value={code}>{issueTypeLabel(code)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Queue</InputLabel>
                        <Select value={queueFilter} label="Queue" onChange={(e) => { setQueueFilter(e.target.value); setPage(0); }}>
                            <MenuItem value="">All</MenuItem>
                            {Object.entries(QUEUE_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Category</InputLabel>
                        <Select value={categoryFilter} label="Category" onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}>
                            <MenuItem value="">All</MenuItem>
                            {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Priority</InputLabel>
                        <Select value={priorityFilter} label="Priority" onChange={(e) => setPriorityFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            {TICKET_PRIORITIES.map((p) => (
                                <MenuItem key={p} value={p}>{PRIORITY_LABELS[p] ?? p}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Date</InputLabel>
                        <Select value={dateFilter} label="Date" onChange={(e) => { setDateFilter(e.target.value); setPage(0); }}>
                            <MenuItem value="">Any time</MenuItem>
                            <MenuItem value="last1h">Last 1 hour</MenuItem>
                            <MenuItem value="last6h">Last 6 hours</MenuItem>
                            <MenuItem value="today">Today</MenuItem>
                            <MenuItem value="yesterday">Yesterday</MenuItem>
                            <MenuItem value="last3">Last 3 days</MenuItem>
                            <MenuItem value="last7">Last 7 days</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Toolbar>

            {/* R2b-4: the desk, automation and quality panels moved to Customer Support → Team & quality. */}

            {/* Second toolbar row: queue tabs (compose with the dropdown filters) + debounced search. */}
            <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={queueTab} onChange={handleQueueTab} sx={{ minHeight: 40 }}>
                    <Tab value="all" label="All" sx={{ minHeight: 40 }} />
                    <Tab value="mine" label="My tickets" sx={{ minHeight: 40 }} />
                    <Tab value="unassigned" label="Unassigned" sx={{ minHeight: 40 }} />
                </Tabs>
                <TextField
                    size="small"
                    placeholder="Search phone / ticket # / text…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={{ width: 280, my: 0.5 }}
                    InputProps={{
                        endAdornment: searchInput ? (
                            <InputAdornment position="end">
                                <IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                    }}
                />
            </Box>

            {busy ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <StyledTableContainer>
                    <Table>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>ID</StyledTableCell>
                                <StyledTableCell>Issue Type</StyledTableCell>
                                <StyledTableCell>Priority</StyledTableCell>
                                <StyledTableCell>Description</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>SLA</StyledTableCell>
                                <StyledTableCell>Created At</StyledTableCell>
                                <StyledTableCell>Requester</StyledTableCell>
                                <StyledTableCell>Related Ride</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((ticket) => (
                                <StyledTableRow
                                    key={ticket.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/support-ticket/${ticket.id}`)}
                                >
                                    <StyledTableCell>{ticket.publicNumber || `#${ticket.id}`}</StyledTableCell>
                                    <StyledTableCell>{issueTypeLabel(ticket.issueType)}</StyledTableCell>
                                    <StyledTableCell>
                                        {ticket.priority
                                            ? <Chip label={PRIORITY_LABELS[ticket.priority] ?? ticket.priority} color={PRIORITY_COLOR[ticket.priority] ?? 'default'} size="small" variant="outlined" />
                                            : '-'}
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {ticket.description}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip label={STATUS_LABELS[ticket.status] ?? ticket.status} color={statusColor(ticket.status) as any} size="small" variant="filled" />
                                    </StyledTableCell>
                                    <StyledTableCell>{renderSla(ticket)}</StyledTableCell>
                                    <StyledTableCell>{new Date(ticket.createdAt).toLocaleString()}</StyledTableCell>
                                    <StyledTableCell onClick={(e) => e.stopPropagation()}>
                                        {ticket.requester ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                                                <span>{ticket.requester.name || 'Unnamed'}{ticket.requester.role ? ` (${ticket.requester.role})` : ''}</span>
                                                {ticket.requester.phone && (
                                                    // masked by the server (§5.11) — open the ticket to reveal and call
                                                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#555' }}>{ticket.requester.phone}</span>
                                                )}
                                            </Box>
                                        ) : (
                                            <span style={{ fontFamily: 'monospace', color: '#999' }}>{ticket.createdByUid.substring(0, 8)}…</span>
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {ticket.chatRoom?.rideId ? `#${ticket.chatRoom.rideId}` : '-'}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {searchActive ? (
                        <Box sx={{ p: 1.5 }}>
                            <Typography variant="caption" color="textSecondary">
                                {searchRows.length} result{searchRows.length === 1 ? '' : 's'} for “{activeQuery}”
                            </Typography>
                        </Box>
                    ) : (
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={PAGE_SIZE}
                            rowsPerPageOptions={[PAGE_SIZE]}
                        />
                    )}
                </StyledTableContainer>
            )}

            <Snackbar
                key={alertMsg?.text}
                open={!!alertMsg}
                autoHideDuration={60000}
                onClose={(_, reason) => {
                    if (reason === 'clickaway') return;              // clicking elsewhere never dismisses
                    if (reason !== 'timeout' && alertLocked) return; // Esc/manual blocked for first 10s
                    setAlertMsg(null);
                }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                {alertMsg ? (
                    <Alert
                        onClose={alertLocked ? undefined : () => setAlertMsg(null)}
                        severity={alertMsg.severity} variant="filled" sx={{ width: '100%' }}
                    >
                        {alertMsg.text}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </StyledPaper>
    );
};

export default SupportTicketList;